import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import { chatApi, authApi, classroomApi, masterAdminApi } from '../services/api';
import { resolvePortalAccessUrl } from '../lib/portalAccessUrl';
import { promptAppInstall } from '../lib/pwaInstall';
import { resolveStudyPartnerFromBio } from '../lib/voiceProfile';
import {
  cancelSpeechPlayback,
  prefetchSpeechAudio,
  prepareSpeechAudio,
  resetAudioPlaybackGate,
  speakLongFormNarration,
  SPOKEN_TEXT_ATTRS,
} from '../lib/speechPlayback';
import { resolveWorkspaceScreenId, MENTEE_KEY_STORAGE, resolveWorkspaceLicenseKey, normalizeClientKey } from '../lib/screenRegistry';
import { ROUTES } from '../lib/classroomRoutes';
import { isMaintenancePreviewEmbed } from '../lib/masterAdminAccess';
import { useMaintenancePreviewMount } from '../lib/maintenancePreviewMountContext';
import LearningModuleEditor from '../components/LearningModuleEditor';
import WorkspaceActivityViewer, { type ActivityOpenTarget } from '../components/WorkspaceActivityViewer';
import { LEARNING_MODULES } from '../lib/programKnowledgeRegistry';
import {
  CERTIFICATE_ACTIVITY_ID,
  getClassroomActivityById,
  isActivityOpenable,
  serializeResourceLinkForClient,
  type SerializedClassroomActivity,
} from '../lib/classroomActivityRegistry';
import { inferViewerTypeFromExtension, toActivityEmbedUrl } from '../lib/activityEmbed';
import './Workspace.css';

const LOGO = encodeURI('/Assets/logo/Mentology Logo.png');

const CLASSROOM_MODULES = LEARNING_MODULES.map(({ moduleId, title }) => ({
  id: moduleId,
  title,
}));
const MODULE_01_ID = CLASSROOM_MODULES[0].id;
const DEFAULT_MENTOR_VOICE_ID = 's116Gqp9aiAQAe29Albd';
const INTRO_WPM = 45;
const INTRO_MS_PER_CHAR = ((60 / INTRO_WPM) * 1000) / 5;

interface Message {
  role: string;
  content: string;
  proteusMode?: string;
}

const MODULE_DRAFTS_STORAGE = 'mentology.moduleDrafts';

interface SavedModuleWork {
  reflection: boolean;
  activity: boolean;
}

interface ModuleActivity {
  id: string;
  label: string;
  kind: string;
}

interface ModuleReflectionPrompt {
  id: string;
  label: string;
}

interface ModuleResourceLink {
  id: string;
  label: string;
  path: string;
}

interface ModuleWorkspace {
  moduleId: string;
  title: string;
  journeyStage: string;
  learningContent: string;
  studyPartnerGuidance: string;
  resourceLinks?: ModuleResourceLink[];
  activities: ModuleActivity[];
  reflectionPrompts: ModuleReflectionPrompt[];
  source: string;
}

interface JournalEntry {
  id: number;
  moduleId: string | null;
  sessionIntent: string | null;
  espousedTheory: string | null;
  theoryInUse: string | null;
  aiMentorAuditFeedback: string | null;
  reflectionBody: string | null;
  submittedAt: string;
}

interface ModuleDraft {
  activity: string;
  reflection: string;
}

async function fetchModuleWorkspace(
  moduleId: string,
  options: { previewEmbed?: boolean } = {},
): Promise<ModuleWorkspace | null> {
  const loadFromMasterAdmin = async (): Promise<ModuleWorkspace | null> => {
    try {
      const data = await masterAdminApi.getLearningModuleConfig(moduleId);
      if (data.workspace) {
        return data.workspace as ModuleWorkspace;
      }
      const config = data.config as {
        moduleId?: string;
        title?: string;
        journeyStage?: string;
        coreContent?: string;
        socraticGuardrails?: string;
        resourceLinks?: ModuleResourceLink[];
      };
      if (!config?.coreContent) return null;
      return buildModuleWorkspaceFromContent({
        moduleId: config.moduleId ?? moduleId,
        title: config.title ?? '',
        journeyStage: config.journeyStage ?? '',
        coreContent: config.coreContent,
        socraticGuardrails: config.socraticGuardrails ?? '',
        resourceLinks: config.resourceLinks ?? [],
      });
    } catch {
      return null;
    }
  };

  if (options.previewEmbed) {
    return loadFromMasterAdmin();
  }

  try {
    const data = await classroomApi.getModule(moduleId);
    if (data?.module) {
      return data.module as ModuleWorkspace;
    }
  } catch {
    // Fall back to master-admin curriculum read when classroom session is unavailable.
  }

  return loadFromMasterAdmin();
}

function stripModuleLearningHeader(text: string) {
  const normalized = text.trim();
  const explicit = normalized.match(/(?:\[MODULE LEARNING\]|(?:^|\n)Module Learning)\s*\n([\s\S]*)$/i);
  if (explicit?.[1]?.trim()) {
    return explicit[1].trim();
  }

  return normalized
    .replace(/(?:^|\n)Module Learning\s*\n?/i, '')
    .replace(/\s*Module Learning\s*$/i, '')
    .trim();
}

function splitModuleContentSections(content: string) {
  const normalized = content.trim();
  const activitiesPattern = /(?:^|\n)(?:\[ACTIVITIES BOX\]|Activities Box)\s*\n/i;
  const activitiesIndex = normalized.search(activitiesPattern);

  if (activitiesIndex >= 0) {
    const beforeActivities = normalized.slice(0, activitiesIndex).trim();
    const afterActivities = normalized
      .slice(activitiesIndex)
      .replace(/^(?:\[ACTIVITIES BOX\]|Activities Box)\s*\n/i, '')
      .trim();

    return {
      learningContent: stripModuleLearningHeader(beforeActivities),
      activitiesContent: afterActivities,
    };
  }

  return {
    learningContent: stripModuleLearningHeader(normalized),
    activitiesContent: '',
  };
}

function extractActivitiesFromContent(content: string): ModuleActivity[] {
  const activities: ModuleActivity[] = [];
  let index = 0;
  const { activitiesContent } = splitModuleContentSections(content);
  const hasActivitiesSection = /\[ACTIVITIES BOX\]/i.test(content)
    || /(?:^|\n)Activities Box/i.test(content)
    || /(?:^|\n)Activities(?:\s*\n|\s*Activity \d+:)/i.test(content);
  const trimmed = content.trim();
  const looksLikeActivitiesSlice =
    /^Activity \d+:/im.test(trimmed)
    || /\[(Activity|Action|Lab Activity)\]/i.test(trimmed)
    || /\[ \]/.test(trimmed);

  const source = hasActivitiesSection
    ? activitiesContent
    : (looksLikeActivitiesSlice ? trimmed : '');

  if (!source.trim()) {
    return activities;
  }

  const numberedBlocks = source
    .split(/\n(?=Activity \d+:)/i)
    .map((block) => block.trim())
    .filter((block) => /^Activity \d+:/i.test(block));

  if (numberedBlocks.length > 0) {
    for (const block of numberedBlocks) {
      index += 1;
      activities.push({
        id: `act-${index}`,
        label: block,
        kind: 'Activity',
      });
    }
    return activities;
  }

  for (const match of source.matchAll(/\[(Activity|Action|Lab Activity)\]:?\s*([\s\S]*?)(?=\n\[(?:Activity|Action|Lab Activity)\]:|\[ \]|$)/gi)) {
    const label = match[2].trim();
    if (!label) continue;
    index += 1;
    activities.push({
      id: `act-${index}`,
      label,
      kind: match[1],
    });
  }

  for (const match of source.matchAll(/\[ \]\s*(.+)/g)) {
    index += 1;
    activities.push({
      id: `act-${index}`,
      label: match[1].trim(),
      kind: 'checklist',
    });
  }

  if (activities.length === 0 && source.trim() && !hasActivitiesSection) {
    activities.push({
      id: 'act-default',
      label: 'Complete the module activities below, then record your responses in the input box.',
      kind: 'general',
    });
  }

  return activities;
}

function extractReflectionPromptsFromContent(content: string): ModuleReflectionPrompt[] {
  const prompts: ModuleReflectionPrompt[] = [];
  let index = 0;
  const { learningContent } = splitModuleContentSections(content);
  const source = learningContent || content;

  for (const match of source.matchAll(/Journal Entry:?\s*([^\n]+)/gi)) {
    index += 1;
    prompts.push({ id: `ref-${index}`, label: match[1].trim() });
  }

  for (const match of source.matchAll(/Reflect:?\s*([^\n]+)/gi)) {
    index += 1;
    prompts.push({ id: `ref-${index}`, label: match[1].trim() });
  }

  if (prompts.length === 0) {
    prompts.push({
      id: 'ref-default',
      label: 'Document your reflection on this module — what shifted in your thinking, and what will you apply next?',
    });
  }

  return prompts;
}

function buildModuleWorkspaceFromContent(input: {
  moduleId: string;
  title: string;
  journeyStage: string;
  coreContent: string;
  socraticGuardrails: string;
  resourceLinks?: ModuleResourceLink[];
}): ModuleWorkspace {
  const { learningContent, activitiesContent } = splitModuleContentSections(input.coreContent);
  return {
    moduleId: input.moduleId,
    title: input.title,
    journeyStage: input.journeyStage,
    learningContent,
    studyPartnerGuidance: input.socraticGuardrails,
    resourceLinks: input.resourceLinks ?? [],
    activities: extractActivitiesFromContent(input.coreContent),
    reflectionPrompts: extractReflectionPromptsFromContent(input.coreContent),
    source: 'Mentology Classroom curriculum (Rev 2)',
  };
}

function linkedActivitiesFromWorkspace(workspace: ModuleWorkspace | null): SerializedClassroomActivity[] {
  if (!workspace?.resourceLinks?.length) {
    return [];
  }

  return workspace.resourceLinks
    .map(serializeResourceLinkForClient)
    .filter((item): item is SerializedClassroomActivity => Boolean(item));
}

interface Bio {
  preferred_name?: string;
  name?: string;
  language_target?: string;
  ai_mentor_gender?: string;
  ai_mentor_name?: string;
  ai_mentor_voice_id?: string;
}

/** Module 01 Welcome and Orientation only — not repeated in other learning modules. */
const MODULE_01_STUDY_PARTNER_INTRO_TEMPLATE =
  "Hello, [NAME], and welcome to the Mentology Classroom learning program. I am your Study Partner, [VOICE]. My role is to bridge the gap between information and true understanding, guiding you step-by-step through your module of study. In this space, there is no passive listening. I am tethered strictly to your program content, so our work remains direct, relevant, and precise. I provide the structural frame; you provide the raw discovery. I will relentlessly push with 'how' and 'why' until your core insights are entirely your own. Every deep session we run will formally lock with a 'Logic Lock'—a definitive, single-sentence synthesis of your structural breakthrough. Our work starts immediately with your Core Discovery Interview. Look down, tap your microphone or use your keyboard, and tell me: what are the primary specific learning goals you want us to smash during this classroom learning program? Unlock your insight.";

function buildModule01StudyPartnerIntro(preferredName: string, studyPartnerVoiceName: string) {
  return MODULE_01_STUDY_PARTNER_INTRO_TEMPLATE
    .replace('[NAME]', preferredName)
    .replace('[VOICE]', studyPartnerVoiceName);
}

function hasModule01UserMessages(messages: Message[]) {
  return messages.some((message) => message.role === 'user');
}

function resolveMenteeName(bio: Bio | null, userName?: string) {
  if (bio?.preferred_name?.trim()) return bio.preferred_name.trim();
  if (bio?.name?.trim()) return bio.name.trim().split(/\s+/)[0];
  if (userName?.trim()) {
    const parts = userName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'there';
    if (parts.length === 1) return parts[0];
    const label = parts[0].toLowerCase();
    if (label === 'corporate' || label === 'private') {
      return parts.length >= 3 ? parts[1] : parts[parts.length - 1];
    }
    return parts[0];
  }
  return 'there';
}

function resolveWorkspaceUserName(bio: Bio | null, userName?: string) {
  if (bio?.preferred_name?.trim()) return bio.preferred_name.trim();
  if (bio?.name?.trim()) return bio.name.trim();
  if (userName?.trim()) {
    const parts = userName.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 3) {
      const label = parts[0].toLowerCase();
      if (label === 'corporate' || label === 'private') {
        return parts.slice(1).join(' ');
      }
    }
    return userName.trim();
  }
  return 'Student';
}

function readPendingBio(): Bio | null {
  try {
    const pendingRaw = sessionStorage.getItem('mentology.pendingBio');
    return pendingRaw ? JSON.parse(pendingRaw) : null;
  } catch {
    return null;
  }
}

function resolveWorkspaceDisplayKey(userToken?: string) {
  const stored = sessionStorage.getItem(MENTEE_KEY_STORAGE);
  const candidate = stored || userToken || '';
  return resolveWorkspaceLicenseKey(candidate);
}

export default function Workspace() {
  const navigate = useNavigate();
  const { user, chatEnabled, setChatEnabled, masterSandboxUnlocked } = useAuth();
  const workspaceScreenId = resolveWorkspaceScreenId(user?.role);
  const previewMount = useMaintenancePreviewMount();
  const isPreviewEmbed = previewMount.active || isMaintenancePreviewEmbed();
  const showLearningSpaceEdit = isPreviewEmbed;
  const displayLicenseKey = resolveWorkspaceDisplayKey(user?.userToken);
  const [bio, setBio] = useState<Bio | null>(readPendingBio);
  const [bioHydrated, setBioHydrated] = useState(false);
  const assignedAiMentorName = resolveStudyPartnerFromBio(bio).name;
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [modulesOpen, setModulesOpen] = useState(true);
  const [journalPanelOpen, setJournalPanelOpen] = useState(false);
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [journalLoading, setJournalLoading] = useState(false);
  const [journalError, setJournalError] = useState('');
  const [finalJournalDraft, setFinalJournalDraft] = useState('');
  const [finalJournalMeta, setFinalJournalMeta] = useState({
    savedAt: null as string | null,
    isFinal: false,
    moduleEntryCount: 0,
  });
  const [finalJournalSaving, setFinalJournalSaving] = useState(false);
  const [finalJournalNotice, setFinalJournalNotice] = useState('');
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);
  const [activeModuleId, setActiveModuleId] = useState<string>(CLASSROOM_MODULES[0].id);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [introDisplay, setIntroDisplay] = useState('');
  const [introComplete, setIntroComplete] = useState(false);
  const [introActive, setIntroActive] = useState(false);
  const [introPreparing, setIntroPreparing] = useState(false);
  const [introNeedsGesture, setIntroNeedsGesture] = useState(false);
  const [localChatEnabled, setLocalChatEnabled] = useState(false);
  const [devBypassActive, setDevBypassActive] = useState(false);
  const [listening, setListening] = useState(false);
  const [reflection, setReflection] = useState('');
  const [moduleWorkspace, setModuleWorkspace] = useState<ModuleWorkspace | null>(null);
  const [moduleLoading, setModuleLoading] = useState(false);
  const [moduleLoadError, setModuleLoadError] = useState('');
  const [moduleEditOpen, setModuleEditOpen] = useState(false);
  const [linkedActivities, setLinkedActivities] = useState<SerializedClassroomActivity[]>([]);
  const [supplementalActivities, setSupplementalActivities] = useState<SerializedClassroomActivity[]>([]);
  const [openActivityId, setOpenActivityId] = useState<string | null>(null);
  const [activityViewerTarget, setActivityViewerTarget] = useState<ActivityOpenTarget | null>(null);
  const [activityOpening, setActivityOpening] = useState(false);
  const [activityOpenError, setActivityOpenError] = useState('');
  const [learningListenState, setLearningListenState] = useState<'idle' | 'loading' | 'playing'>('idle');
  const [moduleDrafts, setModuleDrafts] = useState<Record<string, ModuleDraft>>({});
  const [savedModuleWork, setSavedModuleWork] = useState<Record<string, SavedModuleWork>>({});
  const [synthesis, setSynthesis] = useState<{
    required: boolean;
    report?: { cycle_number: number; report_json: string; completed: number };
    showBilling?: boolean;
  } | null>(null);

  const playbackStarted = useRef(false);
  const introAudioRef = useRef<HTMLAudioElement | null>(null);
  const introRafRef = useRef<number | null>(null);
  const introFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const introPendingPlayRef = useRef<{ script: string; session: number } | null>(null);
  const introRevealIndexRef = useRef(0);
  const bioRef = useRef(bio);
  const userRef = useRef(user);
  bioRef.current = bio;
  userRef.current = user;
  const introScriptRef = useRef('');
  const introVoiceIdRef = useRef<string | null>(null);
  const introModelIdRef = useRef<string | null>(null);
  const introLanguageTargetRef = useRef<string | null>(null);
  const devBypassRef = useRef(false);
  const introFinishedRef = useRef(false);
  const recognitionRef = useRef<{ stop: () => void; start: () => void } | null>(null);
  const journalSaveTimerRef = useRef<number | null>(null);
  const aiTextRef = useRef<HTMLDivElement>(null);
  const learningListenSessionRef = useRef(0);
  const introPlaybackSessionRef = useRef(0);
  const activeModuleIdRef = useRef(activeModuleId);
  activeModuleIdRef.current = activeModuleId;

  const stopLearningNarration = useCallback(() => {
    learningListenSessionRef.current += 1;
    cancelSpeechPlayback();
    setLearningListenState('idle');
  }, []);

  const reloadModuleWorkspace = useCallback(async (moduleId: string) => {
    setModuleLoading(true);
    setModuleLoadError('');

    try {
      const workspace = await fetchModuleWorkspace(moduleId, { previewEmbed: isPreviewEmbed });
      setModuleWorkspace(workspace);
      setLinkedActivities(linkedActivitiesFromWorkspace(workspace));
      if (!workspace) {
        setModuleLoadError(
          isPreviewEmbed
            ? 'Module curriculum could not be loaded in this preview frame. Confirm npm run dev is running (API on port 3001), then refresh.'
            : 'Module curriculum could not be loaded. Restart the dev server (npm run dev) and refresh.',
        );
      }
    } catch {
      setModuleWorkspace(null);
      setLinkedActivities([]);
      setModuleLoadError('Module curriculum could not be loaded.');
    } finally {
      setModuleLoading(false);
    }
  }, [isPreviewEmbed]);

  const reloadLinkedActivities = useCallback(async (moduleId: string) => {
    try {
      const data = await classroomApi.listActivities(moduleId);
      const payload = data as {
        assets?: SerializedClassroomActivity[];
        supplemental?: SerializedClassroomActivity[];
      };
      if (Array.isArray(payload.assets) && payload.assets.length > 0) {
        setLinkedActivities(payload.assets);
      }
      setSupplementalActivities(payload.supplemental ?? []);
    } catch {
      if (moduleWorkspace?.moduleId === moduleId) {
        setLinkedActivities(linkedActivitiesFromWorkspace(moduleWorkspace));
      }
    }
  }, [moduleWorkspace]);

  const handleLearningListen = useCallback(async () => {
    if (learningListenState !== 'idle') {
      stopLearningNarration();
      return;
    }

    const script = moduleWorkspace?.learningContent?.trim();
    if (!script) return;

    const session = learningListenSessionRef.current + 1;
    learningListenSessionRef.current = session;
    setLearningListenState('loading');

    const studyPartner = resolveStudyPartnerFromBio(bio);

    try {
      await speakLongFormNarration(script, studyPartner.voiceId, {
        language: bio?.language_target,
        languageTarget: bio?.language_target,
        gender: bio?.ai_mentor_gender,
        modelId: studyPartner.modelId,
        onPreparingAudio: () => {
          if (learningListenSessionRef.current === session) {
            setLearningListenState('loading');
          }
        },
        onPlaybackStart: () => {
          if (learningListenSessionRef.current === session) {
            setLearningListenState('playing');
          }
        },
      });
    } catch {
      // Playback cancelled or unavailable — fall back to idle.
    } finally {
      if (learningListenSessionRef.current === session) {
        setLearningListenState('idle');
      }
    }
  }, [
    bio,
    learningListenState,
    moduleWorkspace?.learningContent,
    stopLearningNarration,
  ]);

  const clearIntroTimers = useCallback(() => {
    if (introRafRef.current !== null) {
      cancelAnimationFrame(introRafRef.current);
      introRafRef.current = null;
    }
    if (introFallbackTimerRef.current) {
      clearTimeout(introFallbackTimerRef.current);
      introFallbackTimerRef.current = null;
    }
  }, []);

  const resetIntroReveal = useCallback(() => {
    introRevealIndexRef.current = 0;
  }, []);

  const scrollIntroPanelToBottom = useCallback(() => {
    const scrollEl = aiTextRef.current;
    if (!scrollEl) return;
    scrollEl.scrollTop = scrollEl.scrollHeight;
  }, []);

  const stopIntroPlayback = useCallback(() => {
    introPlaybackSessionRef.current += 1;
    setIntroActive(false);
    setIntroNeedsGesture(false);
    introPendingPlayRef.current = null;
    setIntroPreparing(false);
    clearIntroTimers();
    resetIntroReveal();

    if (introAudioRef.current) {
      introAudioRef.current.onended = null;
      introAudioRef.current.pause();
      introAudioRef.current.currentTime = 0;
      introAudioRef.current = null;
    }

    cancelSpeechPlayback();
  }, [clearIntroTimers, resetIntroReveal]);

  const finishIntro = useCallback((script: string) => {
    if (introFinishedRef.current) return;
    introFinishedRef.current = true;
    clearIntroTimers();
    setIntroNeedsGesture(false);
    introPendingPlayRef.current = null;

    if (introAudioRef.current) {
      introAudioRef.current.onended = null;
      introAudioRef.current.pause();
      introAudioRef.current = null;
    }

    cancelSpeechPlayback();
    resetIntroReveal();
    setIntroActive(false);
    setIntroDisplay(script);
    setIntroComplete(true);
    setLocalChatEnabled(true);
    setChatEnabled?.(true);
  }, [clearIntroTimers, resetIntroReveal, setChatEnabled]);

  const runIntroTypewriterFallback = useCallback((script: string, session: number) => {
    resetIntroReveal();
    let index = 0;
    setIntroDisplay('');
    setIntroActive(true);

    const tick = () => {
      if (introPlaybackSessionRef.current !== session || activeModuleIdRef.current !== MODULE_01_ID) {
        return;
      }

      index = Math.min(script.length, index + 1);
      setIntroDisplay((previous) => {
        const next = script.slice(0, index);
        return next.length >= previous.length ? next : previous;
      });
      scrollIntroPanelToBottom();

      if (index >= script.length) {
        finishIntro(script);
        return;
      }

      introFallbackTimerRef.current = setTimeout(tick, INTRO_MS_PER_CHAR);
    };

    tick();
  }, [finishIntro, resetIntroReveal, scrollIntroPanelToBottom]);

  const syncIntroDisplayToAudio = useCallback((script: string, session: number) => {
    const audio = introAudioRef.current;
    if (!audio || introPlaybackSessionRef.current !== session || activeModuleIdRef.current !== MODULE_01_ID) {
      return;
    }

    if (audio.paused || audio.ended) {
      introRafRef.current = requestAnimationFrame(() => {
        syncIntroDisplayToAudio(script, session);
      });
      return;
    }

    if (audio.duration && Number.isFinite(audio.duration) && audio.duration > 0) {
      const progress = Math.min(1, Math.max(0, audio.currentTime / audio.duration));
      const targetIndex = Math.min(script.length, Math.max(0, Math.ceil(progress * script.length)));

      if (targetIndex > introRevealIndexRef.current) {
        introRevealIndexRef.current = targetIndex;
        setIntroDisplay(script.slice(0, targetIndex));
        scrollIntroPanelToBottom();
      }

      if (progress >= 1 || targetIndex >= script.length) {
        finishIntro(script);
        return;
      }
    }

    introRafRef.current = requestAnimationFrame(() => {
      syncIntroDisplayToAudio(script, session);
    });
  }, [finishIntro, scrollIntroPanelToBottom]);

  const beginIntroAudioPlayback = useCallback(async (script: string, session: number) => {
    const audio = introAudioRef.current;
    if (!audio || introPlaybackSessionRef.current !== session || activeModuleIdRef.current !== MODULE_01_ID) {
      return;
    }

    clearIntroTimers();
    resetIntroReveal();
    setIntroNeedsGesture(false);
    introPendingPlayRef.current = null;
    setIntroDisplay('');
    setIntroActive(true);

    audio.onended = () => {
      if (introPlaybackSessionRef.current !== session) return;
      finishIntro(script);
    };

    audio.pause();
    audio.currentTime = 0;

    try {
      await audio.play();
      syncIntroDisplayToAudio(script, session);
    } catch (error) {
      const blocked = error instanceof DOMException && error.name === 'NotAllowedError';
      if (blocked) {
        setIntroNeedsGesture(true);
        introPendingPlayRef.current = { script, session };
        setIntroDisplay('Tap Start Introduction below to hear your Study Partner with synced text.');
        setIntroActive(true);
        return;
      }
      throw error;
    }
  }, [clearIntroTimers, finishIntro, resetIntroReveal, syncIntroDisplayToAudio]);

  const startIntroPlayback = useCallback(async (
    script: string,
    voiceId?: string | null,
    modelId?: string | null,
    languageTarget?: string | null,
  ) => {
    if (activeModuleIdRef.current !== MODULE_01_ID) return;

    stopIntroPlayback();
    const session = introPlaybackSessionRef.current;
    if (activeModuleIdRef.current !== MODULE_01_ID) return;

    introFinishedRef.current = false;
    playbackStarted.current = true;
    introVoiceIdRef.current = voiceId || DEFAULT_MENTOR_VOICE_ID;
    introModelIdRef.current = modelId ?? null;
    introLanguageTargetRef.current = languageTarget ?? bioRef.current?.language_target ?? null;
    introScriptRef.current = script;
    setIntroComplete(false);
    setIntroDisplay('');
    setIntroActive(true);
    setIntroPreparing(true);
    setIntroNeedsGesture(false);

    resetAudioPlaybackGate();

    try {
      const audio = await prepareSpeechAudio(
        introVoiceIdRef.current,
        script,
        introLanguageTargetRef.current ?? undefined,
      );
      if (introPlaybackSessionRef.current !== session || activeModuleIdRef.current !== MODULE_01_ID) {
        audio.pause();
        setIntroPreparing(false);
        return;
      }

      introAudioRef.current = audio;
      setIntroPreparing(false);
      await beginIntroAudioPlayback(script, session);
    } catch (error) {
      console.warn('[Module 01 Study Partner intro sync]', error);
      setIntroPreparing(false);
      if (introPlaybackSessionRef.current !== session || activeModuleIdRef.current !== MODULE_01_ID) {
        return;
      }
      const voiceError = error as { status?: number; code?: string; error?: string };
      const voiceStatus = voiceError?.status;
      if (voiceStatus === 503) {
        setIntroDisplay('Voice is not configured on this server — reading as text.');
      } else if (voiceStatus === 502 && voiceError.code === 'invalid_api_key') {
        setIntroDisplay('Study Partner voice key is invalid on the server — reading as text.');
      } else if (voiceStatus === 502 && voiceError.code === 'quota_exceeded') {
        setIntroDisplay('Study Partner voice credits are exhausted — reading as text.');
      }
      runIntroTypewriterFallback(script, session);
    }
  }, [beginIntroAudioPlayback, runIntroTypewriterFallback, stopIntroPlayback]);

  const resumeIntroAfterGesture = useCallback(() => {
    const pending = introPendingPlayRef.current;
    if (!pending || !introAudioRef.current) return;
    void beginIntroAudioPlayback(pending.script, pending.session);
  }, [beginIntroAudioPlayback]);

  useEffect(() => {
    if (!aiTextRef.current || !introActive) return;
    aiTextRef.current.scrollTop = aiTextRef.current.scrollHeight;
  }, [introDisplay, introActive]);

  useEffect(() => {
    resetAudioPlaybackGate();

    if (isMaintenancePreviewEmbed()) {
      return () => {
        stopIntroPlayback();
        playbackStarted.current = false;
      };
    }

    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      stopIntroPlayback();
      playbackStarted.current = false;
    };
  }, [stopIntroPlayback]);

  useEffect(() => {
    const onDevBypass = (event: KeyboardEvent) => {
      if (!event.ctrlKey || !event.shiftKey || event.key.toLowerCase() !== 'b') return;
      event.preventDefault();

      stopIntroPlayback();

      const menteeName = resolveMenteeName(bioRef.current, userRef.current?.name);
      const studyPartner = resolveStudyPartnerFromBio(bioRef.current);
      const script = buildModule01StudyPartnerIntro(menteeName, studyPartner.name);

      devBypassRef.current = true;
      playbackStarted.current = true;
      introScriptRef.current = script;
      introVoiceIdRef.current = studyPartner.voiceId || DEFAULT_MENTOR_VOICE_ID;
      introModelIdRef.current = studyPartner.modelId;
      introLanguageTargetRef.current = bioRef.current?.language_target ?? null;

      resetAudioPlaybackGate();
      setIntroDisplay(script);
      setIntroComplete(true);
      setLocalChatEnabled(true);
      setChatEnabled?.(true);
      setDevBypassActive(true);
    };

    window.addEventListener('keydown', onDevBypass);
    return () => window.removeEventListener('keydown', onDevBypass);
  }, [setChatEnabled]);

  useEffect(() => {
    authApi.getBio()
      .then((data) => {
        setBio((current) => ({ ...(current || {}), ...(data.bio || {}) }));
        if (data.bio) {
          sessionStorage.removeItem('mentology.pendingBio');
        }
      })
      .catch(() => {})
      .finally(() => setBioHydrated(true));
    chatApi.synthesisStatus().then(setSynthesis).catch(() => {});
  }, []);

  useEffect(() => {
    if (isMaintenancePreviewEmbed()) return;
    if (!user) {
      navigate(ROUTES.CLASSROOM_PORTAL, { replace: true });
      return;
    }

    if (masterSandboxUnlocked) return;

    if (user.role === 'ORG') {
      navigate(ROUTES.SYSTEM_ADMIN, { replace: true });
      return;
    }

    if (user.accountState === 'INITIALIZING') {
      navigate(ROUTES.EXECUTIVE_INTRO, { replace: true });
      return;
    }

    if (user.role === 'PRO' || user.role === 'ELT') {
      if (!bioHydrated) return;
      if (!bio) {
        navigate(ROUTES.STUDENT_SETUP, { replace: true });
        return;
      }
    }

    const storedKey = normalizeClientKey(
      sessionStorage.getItem(MENTEE_KEY_STORAGE) || user.userToken || '',
    );
    if (!resolveWorkspaceLicenseKey(storedKey)) {
      navigate(ROUTES.CLASSROOM_PORTAL, { replace: true });
    }
  }, [masterSandboxUnlocked, user, navigate, bioHydrated, bio]);

  const handleModuleSelect = (moduleId: string) => {
    stopLearningNarration();
    setOpenActivityId(null);
    setActivityViewerTarget(null);
    setActivityOpenError('');
    if (moduleId !== MODULE_01_ID) {
      stopIntroPlayback();
      playbackStarted.current = false;
      introFinishedRef.current = false;
      setIntroDisplay('');
      setIntroComplete(false);
    }
    setMessages([]);
    setInput('');
    setModuleDrafts((current) => ({
      ...current,
      [moduleId]: { activity: '', reflection: '' },
    }));
    setActiveModuleId(moduleId);
  };

  useEffect(() => {
    if (devBypassActive || devBypassRef.current) return;
    if (activeModuleId !== MODULE_01_ID) return;
    if (!user && !isPreviewEmbed) return;
    if (introComplete || hasModule01UserMessages(messages) || introFinishedRef.current) return;

    const menteeName = resolveMenteeName(bioHydrated ? bio : null, user?.name);
    const studyPartner = resolveStudyPartnerFromBio(bioHydrated ? bio : null);
    const script = buildModule01StudyPartnerIntro(menteeName, studyPartner.name);

    void prefetchSpeechAudio(
      studyPartner.voiceId || DEFAULT_MENTOR_VOICE_ID,
      script,
      bio?.language_target ?? undefined,
    ).catch(() => {});
  }, [
    activeModuleId,
    bio,
    user,
    bioHydrated,
    introComplete,
    messages.length,
    devBypassActive,
    isPreviewEmbed,
  ]);

  useEffect(() => {
    if (!bioHydrated || devBypassActive || devBypassRef.current) return;
    if (!user && !isPreviewEmbed) return;
    if (activeModuleId !== MODULE_01_ID) return;

    const menteeName = resolveMenteeName(bio, user?.name);
    const studyPartner = resolveStudyPartnerFromBio(bio);
    const script = buildModule01StudyPartnerIntro(menteeName, studyPartner.name);

    if (hasModule01UserMessages(messages) || introFinishedRef.current) {
      return;
    }

    if (playbackStarted.current && introScriptRef.current !== script) {
      stopIntroPlayback();
      playbackStarted.current = false;
    }

    if (playbackStarted.current) return;

    void startIntroPlayback(script, studyPartner.voiceId, studyPartner.modelId, bio?.language_target ?? null);
  }, [activeModuleId, bio, user, bioHydrated, introComplete, messages.length, startIntroPlayback, devBypassActive, stopIntroPlayback, isPreviewEmbed]);

  useEffect(() => {
    if (activeModuleId === MODULE_01_ID) return;
    stopIntroPlayback();
    playbackStarted.current = false;
    introFinishedRef.current = false;
    setIntroDisplay('');
  }, [activeModuleId, stopIntroPlayback]);

  useEffect(() => {
    stopLearningNarration();
    setModuleEditOpen(false);
  }, [activeModuleId, stopLearningNarration]);

  useEffect(() => {
    let cancelled = false;
    setModuleLoading(true);
    setModuleLoadError('');
    setModuleDrafts((current) => ({
      ...current,
      [activeModuleId]: { activity: '', reflection: '' },
    }));

    fetchModuleWorkspace(activeModuleId, { previewEmbed: isPreviewEmbed })
      .then((workspace) => {
        if (cancelled) return;
        setModuleWorkspace(workspace);
        setLinkedActivities(linkedActivitiesFromWorkspace(workspace));
        if (!workspace) {
          setModuleLoadError(
            isPreviewEmbed
              ? 'Module curriculum could not be loaded in this preview frame. Confirm npm run dev is running (API on port 3001), then refresh.'
              : 'Module curriculum could not be loaded. Restart the dev server (npm run dev) and refresh.',
          );
        }
      })
      .catch(() => {
        if (cancelled) return;
        setModuleWorkspace(null);
        setModuleLoadError('Module curriculum could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setModuleLoading(false);
      });

    classroomApi.getModuleJournalEntry(activeModuleId)
      .then((data) => {
        if (cancelled) return;
        const entry = data.entry as JournalEntry | null;
        const hasReflection = Boolean(entry?.reflectionBody?.trim());
        const hasActivity = Boolean(entry?.theoryInUse?.trim());
        const nextDraft = {
          activity: hasActivity ? entry!.theoryInUse! : '',
          reflection: hasReflection ? entry!.reflectionBody! : '',
        };
        setSavedModuleWork((current) => ({
          ...current,
          [activeModuleId]: { reflection: hasReflection, activity: hasActivity },
        }));
        setModuleDrafts((current) => ({
          ...current,
          [activeModuleId]: nextDraft,
        }));
      })
      .catch(() => {
        if (cancelled) return;
        setSavedModuleWork((current) => ({
          ...current,
          [activeModuleId]: { reflection: false, activity: false },
        }));
      });

    chatApi.getHistory(activeModuleId)
      .then((data) => {
        if (cancelled) return;
        const history = (data.messages || []) as Message[];

        if (activeModuleId === MODULE_01_ID) {
          setMessages(history);
          if (hasModule01UserMessages(history)) {
            const menteeName = resolveMenteeName(bioRef.current, userRef.current?.name);
            const studyPartner = resolveStudyPartnerFromBio(bioRef.current);
            setIntroDisplay(buildModule01StudyPartnerIntro(menteeName, studyPartner.name));
            setIntroComplete(true);
            playbackStarted.current = true;
            introFinishedRef.current = true;
          } else {
            introFinishedRef.current = false;
            playbackStarted.current = false;
            setIntroDisplay('');
            setIntroComplete(false);
          }
          return;
        }

        setMessages(history);
      })
      .catch(() => {
        if (cancelled) return;
        setMessages([]);
      });

    return () => {
      cancelled = true;
    };
  }, [activeModuleId, isPreviewEmbed]);

  useEffect(() => {
    let cancelled = false;

    classroomApi.listActivities(activeModuleId)
      .then((data) => {
        if (cancelled) return;
        const payload = data as {
          assets?: SerializedClassroomActivity[];
          supplemental?: SerializedClassroomActivity[];
        };
        if (Array.isArray(payload.assets) && payload.assets.length > 0) {
          setLinkedActivities(payload.assets);
        }
        setSupplementalActivities(payload.supplemental ?? []);
      })
      .catch(() => {
        if (cancelled) return;
      });

    return () => {
      cancelled = true;
    };
  }, [activeModuleId, moduleWorkspace?.resourceLinks]);

  const buildClientActivityTarget = useCallback((activityId: string): ActivityOpenTarget | null => {
    const linked = [...linkedActivities, ...supplementalActivities].find((item) => item.id === activityId);
    const asset = getClassroomActivityById(activityId);
    if (!asset || !isActivityOpenable(asset)) return null;

    const embedUrl = linked?.url ?? asset.url;
    if (embedUrl) {
      return {
        activityId: asset.id,
        title: asset.title,
        kind: asset.kind,
        viewerType: 'iframe',
        src: toActivityEmbedUrl(embedUrl),
        external: true,
        delivery: asset.delivery,
      };
    }

    if (linked?.resolved === false) {
      return null;
    }

    if (asset.extension) {
      return {
        activityId: asset.id,
        title: asset.title,
        kind: asset.kind,
        viewerType: inferViewerTypeFromExtension(asset.extension),
        src: `/api/classroom/activities/${encodeURIComponent(asset.id)}/file`,
        external: false,
        delivery: asset.delivery,
        fileName: `${asset.fileStem}.${asset.extension}`,
      };
    }

    return null;
  }, [linkedActivities, supplementalActivities]);

  const closeActivityViewer = useCallback(() => {
    setOpenActivityId(null);
    setActivityViewerTarget(null);
    setActivityOpenError('');
  }, []);

  const openLinkedActivity = useCallback(async (activityId: string) => {
    setActivityOpening(true);
    setActivityOpenError('');

    try {
      const data = await classroomApi.openActivity(activityId);
      const target = (data as { target?: ActivityOpenTarget }).target;
      if (!target) {
        throw new Error('missing_target');
      }
      setOpenActivityId(activityId);
      setActivityViewerTarget(target);
      setJournalPanelOpen(false);
      setModuleEditOpen(false);
    } catch {
      const fallback = buildClientActivityTarget(activityId);
      if (fallback) {
        setOpenActivityId(activityId);
        setActivityViewerTarget(fallback);
        setJournalPanelOpen(false);
        setModuleEditOpen(false);
      } else if (activityId === CERTIFICATE_ACTIVITY_ID) {
        setActivityOpenError('Your certificate will appear here once you are marked competent in the mentor assessment.');
      } else {
        setActivityOpenError('This activity file is not available yet. Mount the Mentology Vault (M: drive) and run npm run sync:activities, or ask your facilitator to link it.');
      }
    } finally {
      setActivityOpening(false);
    }
  }, [buildClientActivityTarget]);

  const scheduleModuleJournalSave = useCallback((moduleId: string, draft: ModuleDraft) => {
    if (!draft.reflection.trim() && !draft.activity.trim()) {
      return;
    }
    if (journalSaveTimerRef.current) {
      window.clearTimeout(journalSaveTimerRef.current);
    }
    journalSaveTimerRef.current = window.setTimeout(() => {
      classroomApi.saveModuleJournal(moduleId, {
        reflectionBody: draft.reflection,
        activityBody: draft.activity,
      })
        .then((data) => {
          const entry = data.entry as JournalEntry;
          const hasReflection = Boolean(entry.reflectionBody?.trim());
          const hasActivity = Boolean(entry.theoryInUse?.trim());
          setSavedModuleWork((current) => ({
            ...current,
            [moduleId]: { reflection: hasReflection, activity: hasActivity },
          }));
          setJournalEntries((current) => {
            const others = current.filter((item) => item.moduleId !== entry.moduleId);
            return [...others, entry].sort((a, b) => String(a.moduleId).localeCompare(String(b.moduleId)));
          });
        })
        .catch(() => {});
    }, 800);
  }, []);

  const updateModuleDraft = useCallback((moduleId: string, field: keyof ModuleDraft, value: string) => {
    setModuleDrafts((current) => {
      const nextDraft = {
        activity: current[moduleId]?.activity ?? '',
        reflection: current[moduleId]?.reflection ?? '',
        [field]: value,
      };
      const next = { ...current, [moduleId]: nextDraft };
      scheduleModuleJournalSave(moduleId, nextDraft);
      return next;
    });
  }, [scheduleModuleJournalSave]);

  const loadJournalPanelData = useCallback(async () => {
    setJournalLoading(true);
    setJournalError('');
    setFinalJournalNotice('');
    try {
      const [entriesData, finalData] = await Promise.all([
        classroomApi.listJournalEntries(),
        classroomApi.getFinalJournal(),
      ]);
      const entries = (entriesData.entries || []) as JournalEntry[];
      const finalJournal = finalData.finalJournal as {
        body?: string;
        savedAt?: string | null;
        isFinal?: boolean;
        moduleEntryCount?: number;
      };
      setJournalEntries(entries);
      setFinalJournalMeta({
        savedAt: finalJournal.savedAt ?? null,
        isFinal: Boolean(finalJournal.isFinal),
        moduleEntryCount: finalJournal.moduleEntryCount ?? 0,
      });
      if (finalJournal.body?.trim()) {
        setFinalJournalDraft(finalJournal.body);
      } else if (entries.length > 0) {
        const compiled = await classroomApi.compileJournal();
        setFinalJournalDraft(compiled.compiled || '');
      } else {
        setFinalJournalDraft('');
      }
    } catch {
      setJournalError('Unable to load reflective journal.');
      setJournalEntries([]);
      setFinalJournalDraft('');
    } finally {
      setJournalLoading(false);
    }
  }, []);

  const toggleJournalPanel = useCallback(async () => {
    if (journalPanelOpen) {
      setJournalPanelOpen(false);
      return;
    }
    closeActivityViewer();
    setJournalPanelOpen(true);
    await loadJournalPanelData();
  }, [journalPanelOpen, loadJournalPanelData, closeActivityViewer]);

  const saveFinalJournal = async () => {
    setFinalJournalSaving(true);
    setJournalError('');
    setFinalJournalNotice('');
    try {
      const result = await classroomApi.saveFinalJournal(finalJournalDraft);
      const finalJournal = result.finalJournal as {
        body?: string;
        savedAt?: string | null;
        isFinal?: boolean;
        moduleEntryCount?: number;
      };
      setFinalJournalDraft(finalJournal.body ?? finalJournalDraft);
      setFinalJournalMeta({
        savedAt: finalJournal.savedAt ?? null,
        isFinal: Boolean(finalJournal.isFinal),
        moduleEntryCount: finalJournal.moduleEntryCount ?? 0,
      });
      setFinalJournalNotice(
        finalJournal.isFinal
          ? 'Final journal saved and locked. All 10 module entries are captured.'
          : 'Journal saved. Complete all 10 module reflections to submit your final journal.',
      );
    } catch {
      setJournalError('Unable to save your final journal.');
    } finally {
      setFinalJournalSaving(false);
    }
  };

  const sendMessage = async () => {
    const readyToSend = devBypassActive
      || activeModuleId !== MODULE_01_ID
      || (introComplete && (chatEnabled || localChatEnabled));
    if (!input.trim() || loading || !readyToSend) return;
    const userMsg = input.trim();
    setInput('');

    setModuleDrafts((current) => {
      const prevReflection = current[activeModuleId]?.reflection?.trim() ?? '';
      const nextReflection = prevReflection ? `${prevReflection}\n\n${userMsg}` : userMsg;
      const nextDraft = {
        activity: current[activeModuleId]?.activity ?? '',
        reflection: nextReflection,
      };
      scheduleModuleJournalSave(activeModuleId, nextDraft);
      return { ...current, [activeModuleId]: nextDraft };
    });

    setMessages((m) => [...m, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const result = await chatApi.sendMessage(userMsg, activeModuleId);
      if (result.crisis) {
        setMessages((m) => [...m, { role: 'system', content: result.message }]);
        return;
      }
      setMessages((m) => [...m, {
        role: 'assistant',
        content: result.message.content,
        proteusMode: result.message.proteusMode,
      }]);
    } catch (err: unknown) {
      const e = err as { error?: string };
      setMessages((m) => [...m, { role: 'system', content: e.error || 'Message failed' }]);
    } finally {
      setLoading(false);
    }
  };

  const toggleMic = (
    listeningActive: boolean,
    setListeningActive: (value: boolean) => void,
    recognitionSlot: React.MutableRefObject<{ stop: () => void; start: () => void } | null>,
    onTranscript: (transcript: string) => void,
  ) => {
    const SpeechRecognitionCtor = (window as Window & {
      SpeechRecognition?: new () => {
        continuous: boolean;
        interimResults: boolean;
        lang: string;
        start: () => void;
        stop: () => void;
        onresult: ((event: { results: ArrayLike<{ 0: { transcript: string } }> }) => void) | null;
        onend: (() => void) | null;
        onerror: (() => void) | null;
      };
      webkitSpeechRecognition?: new () => {
        continuous: boolean;
        interimResults: boolean;
        lang: string;
        start: () => void;
        stop: () => void;
        onresult: ((event: { results: ArrayLike<{ 0: { transcript: string } }> }) => void) | null;
        onend: (() => void) | null;
        onerror: (() => void) | null;
      };
    }).SpeechRecognition || (window as Window & { webkitSpeechRecognition?: new () => {
      continuous: boolean;
      interimResults: boolean;
      lang: string;
      start: () => void;
      stop: () => void;
      onresult: ((event: { results: ArrayLike<{ 0: { transcript: string } }> }) => void) | null;
      onend: (() => void) | null;
      onerror: (() => void) | null;
    } }).webkitSpeechRecognition;

    if (!SpeechRecognitionCtor) {
      alert('Voice input is not supported in this browser.');
      return;
    }

    if (listeningActive && recognitionSlot.current) {
      recognitionSlot.current.stop();
      setListeningActive(false);
      return;
    }

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-AU';

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0].transcript)
        .join('');
      onTranscript(transcript);
    };

    recognition.onend = () => setListeningActive(false);
    recognition.onerror = () => setListeningActive(false);

    recognitionSlot.current = recognition;
    recognition.start();
    setListeningActive(true);
  };

  const handleDeleteFiles = async () => {
    setMaintenanceOpen(false);
    if (!confirm('Delete all your files? This cannot be undone.')) return;
    const result = await authApi.deleteData();
    if (!result.success) alert(result.message || result.reason);
    else alert('File deletion initiated.');
  };

  const handleCreateAppIcon = async () => {
    setMaintenanceOpen(false);
    await promptAppInstall();
  };

  const handleShowQrCode = () => {
    setMaintenanceOpen(false);
    setQrModalOpen(true);
  };

  const submitReflection = async () => {
    if (!synthesis?.report) return;
    await chatApi.submitReflection(synthesis.report.cycle_number, reflection);
    setSynthesis(null);
    chatApi.synthesisStatus().then(setSynthesis);
  };

  const isModule01Active = activeModuleId === MODULE_01_ID;
  const module01UserHasReplied = hasModule01UserMessages(messages);
  const showModule01Intro = isModule01Active
    && !module01UserHasReplied
    && !introComplete;
  const canSend = devBypassActive
    || !isModule01Active
    || (introComplete && (chatEnabled || localChatEnabled));
  const activeModule = CLASSROOM_MODULES.find((module) => module.id === activeModuleId) ?? CLASSROOM_MODULES[0];
  const displayUserName = resolveWorkspaceUserName(bio, user?.name);
  const classroomUserKeyLabel = displayLicenseKey
    ? `User: ${displayUserName} ; Key: ${displayLicenseKey}`
    : `User: ${displayUserName}`;
  const moduleTitle = moduleWorkspace?.title || activeModule.title;
  const moduleActivities = moduleWorkspace?.activities ?? [];

  const renderMaintenanceUtilities = () => (
    <>
      {displayLicenseKey && (
        <p className="workspace-nav-meta-line">Subscription: {displayLicenseKey}</p>
      )}
      <button
        type="button"
        className="workspace-nav-utility-btn"
        onClick={() => {
          setMaintenanceOpen(false);
          navigate(ROUTES.STUDENT_SETUP);
        }}
      >
        Change my AI-Study Partner Voice
      </button>
      <button type="button" className="workspace-nav-utility-btn" onClick={handleCreateAppIcon}>
        Add a desktop/homepage Icon
      </button>
      <button type="button" className="workspace-nav-utility-btn" onClick={handleShowQrCode}>
        QR Code to add classroom icon on your Tablet/Phone
      </button>
      <a className="workspace-nav-utility-btn" href="mailto:support@mentology.com.au">
        Contact technical support
      </a>
      <button
        type="button"
        className="workspace-nav-utility-btn workspace-nav-utility-danger"
        onClick={handleDeleteFiles}
      >
        Delete Files
      </button>
    </>
  );

  const renderClassroomNav = () => (
    <nav className="workspace-nav" aria-label="Classroom navigation">
      <div className="workspace-nav-brand">
        <img
          src={LOGO}
          alt="Mentology"
          className="workspace-nav-logo brand-logo"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
        <div className="workspace-nav-heading-block">
          <h1 className="workspace-nav-heading">Classroom</h1>
          <p className="workspace-nav-user-key scannable-bullet-text">{classroomUserKeyLabel}</p>
        </div>
      </div>

      <div className="workspace-nav-accordions">
        <div className="workspace-accordion">
          <button
            type="button"
            className={`workspace-accordion-trigger ${modulesOpen ? 'active' : ''}`}
            aria-expanded={modulesOpen}
            onClick={() => setModulesOpen((open) => !open)}
          >
            Module Selection
            <span aria-hidden="true">{modulesOpen ? '▾' : '▸'}</span>
          </button>
          {modulesOpen && (
            <div className="workspace-accordion-panel">
              {CLASSROOM_MODULES.map((module) => (
                <button
                  key={module.id}
                  type="button"
                  className={`workspace-module-item ${activeModuleId === module.id ? 'active' : ''}`}
                  onClick={() => handleModuleSelect(module.id)}
                >
                  Module {module.id}: {module.title}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="workspace-accordion">
          <button
            type="button"
            className={`workspace-accordion-trigger ${journalPanelOpen ? 'active' : ''}`}
            aria-expanded={journalPanelOpen}
            onClick={() => { void toggleJournalPanel(); }}
          >
            Reflective Journal
            <span aria-hidden="true">{journalPanelOpen ? '▾' : '▸'}</span>
          </button>
        </div>

        <div className="workspace-accordion">
          <button
            type="button"
            className={`workspace-accordion-trigger ${maintenanceOpen ? 'active' : ''}`}
            aria-expanded={maintenanceOpen}
            onClick={() => setMaintenanceOpen((open) => !open)}
          >
            Maintenance
            <span aria-hidden="true">{maintenanceOpen ? '▾' : '▸'}</span>
          </button>
          {maintenanceOpen && (
            <div className="workspace-accordion-panel workspace-maintenance-panel">
              {renderMaintenanceUtilities()}
            </div>
          )}
        </div>
      </div>
    </nav>
  );

  const renderJournalPanel = () => (
    <div className="workspace-journal-panel-stack" aria-label="Reflective Journal workspace">
      <section className="workspace-journal-current-panel">
        <h3 className="workspace-module-panel-heading">Current Journal Entries</h3>
        <div className="workspace-journal-current-scroll workspace-journal-scroll scannable-bullet-text">
          {journalLoading ? (
            <p className="workspace-panel-placeholder">Loading current journal entries...</p>
          ) : journalEntries.length === 0 ? (
            <p className="workspace-panel-placeholder">
              Module reflections and activities auto-save here as you complete each module.
            </p>
          ) : (
            <div className="workspace-journal-current-scroll-inner">
              {journalEntries.map((entry) => (
              <article key={entry.id} className="workspace-journal-current-item">
                <div className="workspace-journal-current-meta">
                  <strong>Module {entry.moduleId}</strong>
                  {entry.submittedAt && (
                    <span>{new Date(entry.submittedAt).toLocaleString('en-AU')}</span>
                  )}
                </div>
                {entry.reflectionBody?.trim() && (
                  <p className="workspace-journal-current-reflection">{entry.reflectionBody}</p>
                )}
                {entry.theoryInUse?.trim() && (
                  <p className="workspace-journal-current-activity">
                    <span className="workspace-journal-current-label">Activity:</span> {entry.theoryInUse}
                  </p>
                )}
              </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="workspace-journal-final-panel">
        <h3 className="workspace-module-panel-heading">Edited Final Journal</h3>
        <div className="workspace-journal-final-body">
          <p className="workspace-journal-final-lead">
            Develop your comprehensive journal from the current module entries above.
            {finalJournalMeta.moduleEntryCount >= 10
              ? ' All 10 modules are captured — save to submit your final journal.'
              : ` ${finalJournalMeta.moduleEntryCount} of 10 module entries captured.`}
          </p>
          {journalError && <p className="workspace-journal-error">{journalError}</p>}
          {finalJournalNotice && <p className="workspace-journal-notice">{finalJournalNotice}</p>}
          <div className="workspace-journal-final-scroll">
            <textarea
              className="workspace-journal-final-editor"
              value={finalJournalDraft}
              onChange={(event) => setFinalJournalDraft(event.target.value)}
              disabled={finalJournalMeta.isFinal}
              placeholder="Compose your edited final journal here..."
              aria-label="Edited final journal"
            />
          </div>
          <button
            type="button"
            className="btn-primary workspace-journal-final-save-btn"
            disabled={finalJournalSaving || finalJournalMeta.isFinal || !finalJournalDraft.trim()}
            onClick={() => { void saveFinalJournal(); }}
          >
            {finalJournalSaving
              ? 'Saving...'
              : finalJournalMeta.isFinal
                ? 'Final Journal Saved'
                : 'Save Final Journal'}
          </button>
        </div>
      </section>
    </div>
  );

  const renderStudentResponseComposer = () => (
    <form
      className="workspace-panel-input-wrap workspace-response-composer"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSend && input.trim() && !loading) sendMessage();
      }}
    >
      <textarea
        className="workspace-panel-input"
        placeholder="Enter assignments, reflections, and Study Partner messages here..."
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (canSend && input.trim() && !loading) sendMessage();
          }
        }}
        disabled={loading}
        aria-label="Assignments, reflections, and Study Partner messages"
      />
      <div className="workspace-panel-input-controls">
        <button
          type="button"
          className={`workspace-mic-btn ${listening ? 'workspace-mic-active' : ''}`}
          onClick={() => toggleMic(listening, setListening, recognitionRef, setInput)}
          disabled={loading}
          aria-label="Microphone"
          title="Microphone"
        >
          🎤
        </button>
        <button
          type="submit"
          className="workspace-send-btn"
          disabled={loading || !canSend || !input.trim()}
        >
          SEND
        </button>
      </div>
    </form>
  );

  if (synthesis?.required && synthesis.report && !synthesis.report.completed) {
    const report = JSON.parse(synthesis.report.report_json);
    return (
      <div className="workspace-screen" data-screen-id={workspaceScreenId}>
        {renderClassroomNav()}
        <main className="workspace-learning">
          <h2 className="workspace-learning-heading">Learning Space</h2>
          <div className="workspace-learning-scroll">
            <div className="workspace-reflection">
              <h3 className="workspace-reflection-title">Day 28 Synthesis</h3>
              <div className="workspace-bubble workspace-bubble-assistant scannable-bullet-text">
                {report.actionLearningLoops?.map((loop: { phase: string; summary: string }) => (
                  <p key={loop.phase}><strong>{loop.phase}:</strong> {loop.summary}</p>
                ))}
              </div>
              <textarea
                className="input-field workspace-reflection-input"
                placeholder="Your evaluation response..."
                value={reflection}
                onChange={(e) => setReflection(e.target.value)}
              />
              <button type="button" className="btn-primary workspace-reflection-btn" onClick={submitReflection}>
                Submit Reflection
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="workspace-screen" data-screen-id={workspaceScreenId}>
      {renderClassroomNav()}

      <main className="workspace-learning">
        <div className="workspace-learning-header-row">
          <h2 className="workspace-learning-heading">
            {journalPanelOpen
              ? 'Reflective Journal'
              : openActivityId && activityViewerTarget
                ? activityViewerTarget.title
                : 'Learning Space'}
          </h2>
          <div className="workspace-learning-header-actions">
            {(journalPanelOpen || openActivityId) && (
              <button
                type="button"
                className="workspace-journal-back-btn"
                onClick={() => {
                  if (openActivityId) {
                    closeActivityViewer();
                    return;
                  }
                  setJournalPanelOpen(false);
                }}
              >
                Back to Learning Space
              </button>
            )}
            {showLearningSpaceEdit && !journalPanelOpen && !openActivityId && (
              <button
                type="button"
                className="workspace-edit-btn"
                onClick={() => setModuleEditOpen((open) => !open)}
              >
                {moduleEditOpen ? 'CLOSE EDIT' : 'EDIT'}
              </button>
            )}
          </div>
        </div>

        {journalPanelOpen ? renderJournalPanel() : openActivityId && activityViewerTarget ? (
          <WorkspaceActivityViewer target={activityViewerTarget} onBack={closeActivityViewer} />
        ) : moduleEditOpen && showLearningSpaceEdit ? (
          <div className="workspace-module-editor-mount">
            <LearningModuleEditor
              moduleId={activeModuleId}
              onBack={() => setModuleEditOpen(false)}
              onSaved={() => {
                void reloadModuleWorkspace(activeModuleId);
                void reloadLinkedActivities(activeModuleId);
              }}
            />
          </div>
        ) : (
        <div className="workspace-module-stack">
          <section
            className="workspace-module-panel workspace-module-panel-learning-activities"
            aria-label="Module Learning and Activities"
          >
            <div className="workspace-module-panel-heading-row">
              <h3 className="workspace-module-panel-heading">
                Module Learning &amp; Activities
                <span className="workspace-module-panel-subtitle">
                  Module {activeModule.id}: {moduleTitle}
                </span>
              </h3>
              <button
                type="button"
                className={`workspace-listen-btn ${
                  learningListenState === 'loading' ? 'is-loading' : ''
                } ${learningListenState === 'playing' ? 'is-active' : ''}`}
                aria-busy={learningListenState === 'loading'}
                aria-label={
                  learningListenState === 'loading'
                    ? 'Preparing module learning audio'
                    : learningListenState === 'playing'
                      ? 'Stop reading module learning aloud'
                      : 'Listen to module learning with Study Partner voice'
                }
                title={
                  learningListenState === 'loading'
                    ? 'Preparing audio…'
                    : learningListenState === 'playing'
                      ? 'Stop listening'
                      : 'Listen to module learning'
                }
                disabled={moduleLoading || !moduleWorkspace?.learningContent}
                onClick={() => { void handleLearningListen(); }}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" className="workspace-listen-icon">
                  <path
                    d="M11 5 6 9H3v6h3l5 4V5zm2.14 12.86a5 5 0 0 0 0-7.72M13.07 4.93a9 9 0 0 1 0 14.14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <div className="workspace-panel-body scannable-bullet-text">
              {moduleLoading ? (
                <p className="workspace-panel-placeholder">Loading module content...</p>
              ) : moduleWorkspace?.learningContent ? (
                <div className="workspace-module-learning-content">{moduleWorkspace.learningContent}</div>
              ) : (
                <p className="workspace-panel-placeholder">
                  {moduleLoadError || 'Module readings from the Mentology curriculum will appear here after you sign in.'}
                </p>
              )}

              <h4 className="workspace-learning-activities-divider">Activities</h4>

              {(linkedActivities.length > 0 || supplementalActivities.length > 0) && (
                <div className="workspace-linked-activities">
                  <p className="workspace-linked-activities-label">Open form / reading</p>
                  <ul className="workspace-linked-activity-list">
                    {linkedActivities.map((activity) => (
                      <li key={activity.id}>
                        <button
                          type="button"
                          className="workspace-linked-activity-btn"
                          disabled={!activity.openable || activityOpening}
                          onClick={() => { void openLinkedActivity(activity.id); }}
                        >
                          {activity.title}
                        </button>
                      </li>
                    ))}
                    {supplementalActivities.map((activity) => (
                      <li key={activity.id}>
                        <button
                          type="button"
                          className="workspace-linked-activity-btn workspace-linked-activity-btn-supplemental"
                          disabled={!activity.openable || activityOpening}
                          onClick={() => { void openLinkedActivity(activity.id); }}
                        >
                          {activity.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {activityOpenError && (
                <p className="workspace-activity-open-error">{activityOpenError}</p>
              )}
              {moduleActivities.length > 0 && (
                <ul className="workspace-activity-list scannable-bullet-text">
                  {moduleActivities.map((activity) => (
                    <li key={activity.id} className="workspace-activity-item">
                      {activity.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section
            className={`workspace-module-panel workspace-module-panel-response ${SPOKEN_TEXT_ATTRS.className}`}
            aria-label="Reflections and Study Partner"
            data-module-id={activeModuleId}
          >
            <h3 className="workspace-module-panel-heading">Reflections &amp; Study Partner</h3>
            <div className="workspace-panel-body workspace-response-body">
              <div ref={aiTextRef} className="workspace-response-scroll scannable-bullet-text">
                <div className="workspace-thread">
                  {showModule01Intro && (
                    <div
                      className={`workspace-bubble workspace-bubble-assistant workspace-module01-intro scannable-bullet-text ${SPOKEN_TEXT_ATTRS.className}`}
                      aria-live="polite"
                    >
                      <span className={`workspace-module01-intro-content${introPreparing && !introDisplay ? ' workspace-module01-intro-preparing' : ''}`}>
                        {introPreparing && !introDisplay
                          ? 'Preparing Study Partner voice…'
                          : introDisplay}
                      </span>
                      {introNeedsGesture && (
                        <button
                          type="button"
                          className="workspace-intro-start-btn"
                          onClick={resumeIntroAfterGesture}
                        >
                          Start Introduction
                        </button>
                      )}
                      {!introComplete && !introNeedsGesture && !introPreparing && (
                        <span className="workspace-intro-typing-cursor" aria-hidden="true">▋</span>
                      )}
                    </div>
                  )}
                  {messages.map((m, i) => (
                    <div
                      key={i}
                      className={`workspace-bubble workspace-bubble-${m.role} scannable-bullet-text ${SPOKEN_TEXT_ATTRS.className}`}
                    >
                      {m.content}
                    </div>
                  ))}
                  {loading && (
                    <p className="workspace-bubble workspace-bubble-assistant scannable-bullet-text">Processing...</p>
                  )}
                </div>
              </div>

              {renderStudentResponseComposer()}
            </div>
          </section>
        </div>
        )}
      </main>

      {qrModalOpen && (
        <div
          className="workspace-qr-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="workspace-qr-title"
          onClick={() => setQrModalOpen(false)}
        >
          <div
            className="workspace-qr-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="workspace-qr-title" className="section-header">Mobile Access QR Code</h2>
            <p className="workspace-qr-url scannable-bullet-text">{resolvePortalAccessUrl()}</p>
            <div className="workspace-qr-code-wrap" aria-hidden="true">
              <QRCodeSVG
                value={resolvePortalAccessUrl()}
                size={220}
                level="M"
                bgColor="#ffffff"
                fgColor="#000000"
                includeMargin
              />
            </div>
            <button type="button" className="btn-primary workspace-qr-close" onClick={() => setQrModalOpen(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
